import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '@/components/layout/Header';
import { useInitWedding } from '@/hooks/useWedding';
import { useWedding } from '@/hooks/useWedding';
import { useParse } from '@/hooks/useParse';
import { useToast } from '@/hooks/useToast';
import { track } from '@/lib/analytics';

export function PasteImportPage() {
  const [, navigate] = useLocation();
  const { weddingId } = useInitWedding();
  const { data: wedding } = useWedding(weddingId);
  const parseMutation = useParse();
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');

  // Auto-focus textarea on mount
  useEffect(() => {
    const id = setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
    return () => clearTimeout(id);
  }, []);

  async function handleSubmit() {
    if (!text.trim()) return;
    track('import_started', { method: 'paste' });
    try {
      const result = await parseMutation.mutateAsync({
        content: text,
        contentType: 'text',
        weddingDate: wedding?.weddingDate ?? undefined,
      });
      sessionStorage.setItem('wedtrack:parsed', JSON.stringify(result.events));
      navigate('/schedule/import/review');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse timeline';
      toast(message, 'error');
    }
  }

  const isPending = parseMutation.isPending;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--color-background)',
      }}
    >
      <Header title="Paste Timeline" backHref="/schedule" />

      {/* Content */}
      <div style={{ flex: 1, padding: '16px 16px 120px' }}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your wedding timeline here…"
          style={{
            width: '100%',
            minHeight: 240,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
            color: 'var(--color-text)',
            fontFamily: 'var(--font-body)',
            fontSize: 15,
            lineHeight: 1.6,
            resize: 'none',
            outline: 'none',
            transition: 'border-color 150ms ease',
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLTextAreaElement).style.borderColor =
              'var(--color-border-focus)';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLTextAreaElement).style.borderColor =
              'var(--color-border)';
          }}
        />

        <p
          style={{
            fontSize: 13,
            color: 'var(--color-text-muted)',
            marginTop: 10,
            lineHeight: 1.5,
          }}
        >
          Tip: Most formats work — bullet lists, tables, time ranges, paragraphs.
        </p>
      </div>

      {/* Bottom sticky bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 512,
          padding: '12px 16px max(16px, env(safe-area-inset-bottom))',
          background: 'linear-gradient(to top, var(--color-background) 70%, transparent)',
          zIndex: 100,
        }}
      >
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || isPending}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 'var(--radius-lg)',
            border: 'none',
            background: !text.trim() || isPending ? 'rgba(212,168,75,0.3)' : 'var(--color-primary)',
            color: !text.trim() || isPending ? 'rgba(212,168,75,0.5)' : '#1A1A2E',
            fontSize: 15,
            fontWeight: 600,
            cursor: !text.trim() || isPending ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)',
            transition: 'background 150ms ease, color 150ms ease',
          }}
        >
          Parse Timeline
        </button>
      </div>

      {/* Loading overlay */}
      {isPending && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            background: 'rgba(26, 26, 46, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          {/* Spinner */}
          <div
            className="animate-spin"
            style={{
              width: 40,
              height: 40,
              border: '3px solid rgba(212,168,75,0.2)',
              borderTopColor: 'var(--color-primary)',
              borderRadius: '50%',
            }}
          />
          <p
            style={{
              fontSize: 15,
              color: 'var(--color-text-secondary)',
              margin: 0,
            }}
          >
            Parsing your timeline…
          </p>
        </div>
      )}
    </div>
  );
}
